'use client';

import React, { useCallback, useMemo } from 'react';
import { Spinner, Chip } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { EmptyContentIc } from '../../../../components/icons';
import { CampaignsTableProps, RevshareCampaign } from '../types';
import { getTableColumns } from '../constants';
import IdCell from './IdCell';
import CampaignCell from './CampaignCell';
import StatisticsCell from './StatisticsCell';
import IncomeCell from './IncomeCell';
import StatusCell from './StatusCell';
import ActionsCell from './ActionsCell';

const CampaignsTable: React.FC<CampaignsTableProps> = ({
  campaigns,
  isLoading,
  totalCount,
  totalIncome,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onShowContent,
  onStopCampaign,
  onEditCampaign,
  onRefresh,
}) => {
  const { t, lang } = useTranslations();

  const renderCell = useCallback(
    (campaign: RevshareCampaign, columnKey: string) => {
      switch (columnKey) {
        case 'id':
          return (
            <IdCell campaign={campaign} onEditCampaign={onEditCampaign} onRefresh={onRefresh} />
          );
        case 'campaign':
          return <CampaignCell campaign={campaign} />;
        case 'statistics':
          return <StatisticsCell campaign={campaign} />;
        case 'income':
          return <IncomeCell campaign={campaign} />;
        case 'status':
          return <StatusCell campaign={campaign} />;
        case 'actions':
          return (
            <ActionsCell
              campaign={campaign}
              onShowContent={onShowContent}
              onStopCampaign={onStopCampaign}
            />
          );
        default:
          return null;
      }
    },
    [onShowContent, onStopCampaign, onEditCampaign, onRefresh]
  );

  // Custom columns with income header badge
  const columns = useMemo(() => {
    return getTableColumns(t).map((col) => {
      if (col.key === 'income') {
        return {
          ...col,
          name: (
            <div className="flex items-center gap-2">
              <span>{col.name}</span>
              <Chip
                size="sm"
                classNames={{
                  base: 'bg-[rgba(212,212,216,0.4)] h-6 px-2',
                  content: 'text-auth-muted text-xs font-medium p-0',
                }}
              >
                ${totalIncome.toFixed(2)}
              </Chip>
            </div>
          ),
        };
      }
      return col;
    });
  }, [t, totalIncome]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner
          size="lg"
          classNames={{
            circle1: '!border-b-partner',
            circle2: '!border-b-partner/50',
          }}
        />
      </div>
    );
  }

  return (
    <UniversalTable
      key={lang}
      data={campaigns}
      columns={columns}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('partner.partnerCampaigns.noCampaigns')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('partner.partnerCampaigns.noCampaignsDescription')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default CampaignsTable;
